import { useContext, useEffect, useState } from "react";
import { Dropdown, Modal, Tag, Tooltip } from "antd";
import {
	DownOutlined,
	EditOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
	StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import trans, { formatDuration } from "../../../utils";
import AntdTable from "../../antd/Table/Table";
import { popupcontext } from "../../../context/Popupscontext";
import { filtrationcontext } from "../../../context/Filtercontext";
import usePermission from "../../../hooks/usePermission";
import {
	useGetAllRecurringPlannersQuery,
	usePauseResumeRecurringPlannerMutation,
	useCancelRecurringPlannerMutation,
} from "../../../Redux/service/RecurringPlanners/RecurringPlanners";
import WeekDayChips from "../WeekDayChips";
import ViewPlannerDrawer from "../Popups/ViewPlannerDrawer";

export const PLANNER_STATUS_COLORS = {
	ACTIVE: "green",
	PAUSED: "orange",
	CANCELLED: "red",
};

export const PRIORITY_COLORS = {
	low: "blue",
	medium: "gold",
	high: "red",
};

export const formatPlannerTime = (time) => (time ? time.slice(0, 5) : "--");

const RecurringPlannersList = ({ statusFilter }) => {
	const { edited, setEdited, handleShow } = useContext(popupcontext);
	const { limit, setLimit, searchQuery } = useContext(filtrationcontext);
	const { edit: canEdit, delete: canDelete } = usePermission("FIELD_TASKS");

	const [currentPage, setCurrentPage] = useState(1);
	const [sort, setSort] = useState({ by: "", order: "" });
	const [viewPlannerId, setViewPlannerId] = useState(null);
	const [cancelTarget, setCancelTarget] = useState(null);

	const {
		data: plannersData,
		isFetching,
		refetch,
	} = useGetAllRecurringPlannersQuery(
		{
			page: currentPage,
			limit,
			search: searchQuery,
			sortBy: sort.by,
			sortOrder: sort.order,
			status: statusFilter,
		},
		{ refetchOnMountOrArgChange: true }
	);

	const [pauseResumePlanner, { isLoading: isToggling }] =
		usePauseResumeRecurringPlannerMutation();
	const [cancelPlanner, { isLoading: isCancelling }] =
		useCancelRecurringPlannerMutation();

	useEffect(() => {
		setCurrentPage(1);
	}, [statusFilter, searchQuery]);

	useEffect(() => {
		async function refetchAfterSave() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
		}
		refetchAfterSave();
	}, [edited, refetch, setEdited]);

	const notify = (message, type = "success") => {
		toast[type](message, {
			autoClose: 800,
			rtl: localStorage.getItem("lang") === "ar" ? true : false,
		});
	};

	const handlePauseResume = async (planner) => {
		const action = planner.status === "ACTIVE" ? "pause" : "resume";
		try {
			await pauseResumePlanner({ id: planner.id, action }).unwrap();
			notify(
				action === "pause"
					? trans("Planner paused successfully")
					: trans("Planner resumed successfully")
			);
			refetch();
		} catch (err) {
			notify(trans(err?.data?.message ?? "Something went wrong"), "error");
		}
	};

	const handleCancelConfirm = async () => {
		try {
			await cancelPlanner(cancelTarget.id).unwrap();
			notify(trans("Planner cancelled successfully"));
			setCancelTarget(null);
			const res = await refetch();
			if ((res?.data?.data || []).length === 0 && currentPage > 1) {
				setCurrentPage((prev) => prev - 1);
			}
		} catch (err) {
			notify(trans(err?.data?.message ?? "Something went wrong"), "error");
		}
	};

	const buildActionItems = (planner) => {
		const items = [];
		if (canEdit && planner.status !== "CANCELLED") {
			items.push({
				label: trans("Edit"),
				key: "edit",
				icon: <EditOutlined />,
			});
			items.push({
				label:
					planner.status === "ACTIVE" ? trans("Pause") : trans("Resume"),
				key: "toggle",
				icon:
					planner.status === "ACTIVE" ? (
						<PauseCircleOutlined />
					) : (
						<PlayCircleOutlined />
					),
			});
		}
		if (canDelete && planner.status !== "CANCELLED") {
			items.push({
				label: trans("Cancel Planner"),
				key: "cancel",
				icon: <StopOutlined />,
				danger: true,
			});
		}
		return items;
	};

	const handleMenuClick = (key, planner) => {
		if (key === "edit") handleShow("editForm", "recurringPlanner", planner.id);
		if (key === "toggle") handlePauseResume(planner);
		if (key === "cancel") setCancelTarget(planner);
	};

	const columns = [
		{
			title: trans("Planner Name"),
			dataIndex: "name",
			key: "name",
			sorter: true,
			fixed: "left",
			width: 200,
			render: (name) => <span>{name}</span>,
		},
		{
			title: trans("Schedule"),
			dataIndex: "daysOfWeek",
			key: "daysOfWeek",
			width: 220,
			render: (daysOfWeek) => <WeekDayChips daysOfWeek={daysOfWeek} />,
		},
		{
			title: trans("Time"),
			dataIndex: "startTime",
			key: "startTime",
			width: 100,
			render: (value) => <span>{formatPlannerTime(value)}</span>,
		},
		{
			title: trans("Duration"),
			dataIndex: "expectedDuration",
			key: "expectedDuration",
			width: 120,
			render: (value) => <span>{formatDuration(value)}</span>,
		},
		{
			title: trans("Period"),
			dataIndex: "startDate",
			key: "startDate",
			sorter: true,
			width: 220,
			render: (_, record) => (
				<span>
					{dayjs(record.startDate).format("DD MMM YYYY")}
					{" → "}
					{record.endDate
						? dayjs(record.endDate).format("DD MMM YYYY")
						: trans("Ongoing")}
				</span>
			),
		},
		{
			title: trans("Type"),
			dataIndex: "type",
			key: "type",
			sorter: true,
			width: 120,
			render: (value) => (
				<span className="text-capitalize">{trans(value) || "No data"}</span>
			),
		},
		{
			title: trans("Priority"),
			dataIndex: "priority",
			key: "priority",
			sorter: true,
			width: 110,
			render: (value) => (
				<Tag
					color={PRIORITY_COLORS[value] || "default"}
					className="text-capitalize"
				>
					{trans(value)}
				</Tag>
			),
		},
		{
			title: trans("status"),
			dataIndex: "status",
			key: "status",
			sorter: true,
			width: 120,
			render: (value) => (
				<Tag color={PLANNER_STATUS_COLORS[value] || "default"}>
					{trans(value)}
				</Tag>
			),
		},
		{
			title: trans("Assigned To"),
			dataIndex: "assignedTo",
			key: "assignedTo",
			width: 180,
			render: (value) => (
				<span className="text-capitalize">
					{value?.name || trans("Unassigned")}
				</span>
			),
		},
		{
			title: trans("POI Name"),
			dataIndex: "poi",
			key: "poi",
			width: 180,
			render: (poi) => <span>{poi?.name || trans("Not assigned")}</span>,
		},
		{
			title: trans("Forms"),
			dataIndex: "forms",
			key: "forms",
			width: 100,
			render: (forms) =>
				forms?.length ? (
					<Tooltip
						title={forms
							.map((f) => f?.form?.name)
							.filter(Boolean)
							.join(", ")}
					>
						<Tag color="green">{forms.length}</Tag>
					</Tooltip>
				) : (
					<span className="text-muted">—</span>
				),
		},
		{
			title: trans("Created By"),
			dataIndex: "createdBy",
			key: "createdBy",
			width: 150,
			render: (value) => (
				<span className="text-capitalize">{value?.name || "No data"}</span>
			),
		},
		{
			title: trans("Actions"),
			key: "actions",
			width: 140,
			render: (_, record) => (
				<Dropdown.Button
					icon={<DownOutlined />}
					className="custom-dropdown-button"
					menu={{
						items: buildActionItems(record),
						onClick: ({ key }) => handleMenuClick(key, record),
					}}
					onClick={() => setViewPlannerId(record.id)}
				>
					{trans("view")}
				</Dropdown.Button>
			),
		},
	];

	const handleTableChange = (pagination, filters, sorter) => {
		if (sorter.order) {
			setSort({
				by: sorter.field,
				order: sorter.order === "ascend" ? "asc" : "desc",
			});
		} else {
			setSort({ by: "", order: "" });
		}
	};

	return (
		<div className="bg-white-rounded-top mt-3 table-parent">
			<AntdTable
				columns={columns}
				dataSource={plannersData?.data || []}
				loading={isFetching || isToggling}
				pageSize={limit}
				totalItems={plannersData?.total ?? 0}
				currentPage={currentPage}
				onPageChange={(page) => setCurrentPage(page)}
				onLimitChange={(newLimit) => {
					setLimit(newLimit);
					setCurrentPage(1);
				}}
				onTableChange={handleTableChange}
			/>

			<ViewPlannerDrawer
				plannerId={viewPlannerId}
				onClose={() => setViewPlannerId(null)}
				canEdit={canEdit}
				onPauseResume={async (planner) => {
					await handlePauseResume(planner);
				}}
			/>

			<Modal
				title={trans("Cancel Recurring Planner")}
				open={!!cancelTarget}
				onOk={handleCancelConfirm}
				onCancel={() => setCancelTarget(null)}
				okText={trans("Cancel Planner")}
				cancelText={trans("Keep Planner")}
				okButtonProps={{ danger: true, loading: isCancelling }}
			>
				<p>
					{trans(
						"Are you sure you want to cancel this planner? No new tasks will be generated from it. This action cannot be undone."
					)}
				</p>
				{cancelTarget && <strong>{cancelTarget.name}</strong>}
			</Modal>
		</div>
	);
};

export default RecurringPlannersList;
